<template>
  <Teleport to="body">
    <TransitionGroup
      tag="div"
      class="fixed z-[999999] flex flex-col gap-3 p-6 bottom-0 right-0 items-end pointer-events-none"
      name="feedback-toast"
    >
      <div
        v-for="toast in visibleToasts"
        :key="toast.id"
        class="bg-white rounded-xl shadow-[0_4px_12px_0_rgba(0,0,0,0.08)] px-3.5 py-4 flex flex-col gap-5 w-[360px] pointer-events-auto"
      >
        <!-- Header -->
        <div class="flex flex-col gap-5">
          <div class="flex items-center gap-4">
            <!-- Icon Container -->
            <div
              :class="[
                'flex items-center justify-center w-8 h-8 rounded-lg border shrink-0',
                variantIconClasses[toast.variant || 'success']
              ]"
            >
              <!-- Success Icon -->
              <svg v-if="toast.variant === 'success' || !toast.variant" class="w-5 h-5" viewBox="0 0 20 20" fill="none">
                <path d="M16.6667 5.41667L7.5 14.5833L3.33333 10.4167" stroke="#1DC37B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <!-- Error Icon -->
              <svg v-else-if="toast.variant === 'error'" class="w-5 h-5" viewBox="0 0 20 20" fill="none">
                <path d="M6.66667 6.66667L13.3333 13.3333M13.3333 6.66667L6.66667 13.3333" stroke="#FE5E86" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <!-- Warning Icon -->
              <svg v-else-if="toast.variant === 'warning'" class="w-5 h-5" viewBox="0 0 20 20" fill="none">
                <path d="M10 7V10.5M10 13.5V13.51" stroke="#F59E0B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="#F59E0B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <!-- Info Icon -->
              <svg v-else-if="toast.variant === 'info'" class="w-5 h-5" viewBox="0 0 20 20" fill="none">
                <path d="M10 10V14M10 6V6.01" stroke="#3B82F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="#3B82F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <!-- Title -->
            <p class="flex-1 font-mont text-lg font-semibold leading-6 text-grey-900 tracking-title-sm">
              {{ toast.title }}
            </p>

            <!-- Close Button -->
            <button
              type="button"
              class="p-2 rounded-lg hover:bg-grey-50 transition-colors shrink-0"
              @click="removeToast(toast.id)"
            >
              <svg class="w-5 h-5 text-grey-800" viewBox="0 0 20 20" fill="none">
                <path d="M6.66667 6.66667L13.3333 13.3333M13.3333 6.66667L6.66667 13.3333" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <!-- Description -->
          <p v-if="toast.description" class="text-sm text-grey-600 tracking-tooltip leading-relaxed">
            {{ toast.description }}
          </p>
        </div>

        <!-- Actions -->
        <div v-if="toast.primaryAction || toast.secondaryAction" class="flex items-center justify-end gap-5">
          <button
            v-if="toast.secondaryAction"
            type="button"
            class="h-8 px-3 py-1.5 bg-white border border-grey-200 rounded-lg text-sm text-grey-900 tracking-status-badge hover:bg-grey-50 transition-colors"
            @click="handleSecondaryAction(toast)"
          >
            {{ toast.secondaryAction.label }}
          </button>
          <button
            v-if="toast.primaryAction"
            type="button"
            class="h-[27px] px-3 py-1.5 bg-royal-500 rounded-md text-xs text-white tracking-status-badge hover:bg-royal-600 transition-colors"
            @click="handlePrimaryAction(toast)"
          >
            {{ toast.primaryAction.label }}
          </button>
        </div>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useFeedbackToast, type FeedbackToast } from '../../composables/useFeedbackToast'

const { toasts, maxVisible, removeToast } = useFeedbackToast()

const visibleToasts = computed(() => toasts.value.slice(0, maxVisible.value))

const variantIconClasses = {
  success: 'bg-[#ECFDF5] border-[#6EE7B7]',
  error: 'bg-[#FEF2F2] border-[#FECACA]',
  warning: 'bg-[#FFFBEB] border-[#FCD34D]',
  info: 'bg-[#EFF6FF] border-[#93C5FD]',
}

const handlePrimaryAction = (toast: FeedbackToast) => {
  toast.primaryAction?.onClick?.()
  removeToast(toast.id)
}

const handleSecondaryAction = (toast: FeedbackToast) => {
  toast.secondaryAction?.onClick?.()
  removeToast(toast.id)
}
</script>

<style>
.feedback-toast-enter-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.feedback-toast-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.feedback-toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.feedback-toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.feedback-toast-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
